/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {memo, useCallback, useEffect, useRef} from 'react';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import {mapViewGoToOptions} from '../constants';
import {DEFAULT_SYMBOL} from '../geofences/constants';
import {initializeEsriModules} from '../helpers/initializeEsriModules';
import {removeLayerIfExist} from '../helpers/MapViewUtils';
import {BoundaryLayerProps} from '../types/types';
import {AssetLayerProps} from './AssetLayer';

// defines the prop definitions for the BoundaryLayer
type Props = {
  map?: __esri.Map;
  view?: __esri.MapView;
  isFMTracking?: boolean;
  boundaryLayerProps: BoundaryLayerProps
  assetLayerProps?: AssetLayerProps;
};

// Unique identifier for the boundary layer.
export const BOUNDARY_MAP_LAYER_ID = 'boundaryMapLayer';

const getEsriModules = async(isFMTracking: boolean = false) => {
  if (!isFMTracking) {
    const [
      Graphic,
      GraphicsLayer
    ] = await initializeEsriModules('esri/Graphic', 'esri/layers/GraphicsLayer');
    return {Graphic, GraphicsLayer};
  }
  return {Graphic, GraphicsLayer};
};

/**
 * @BoundaryLayer
 *
 * Defines the BoundaryLayer which manages rendering a layer with boundary graphics.
 *
 * ```typescript
 *
 *   import {ResponsiveBaseMapLayer, BoundaryLayer} from './layers';
 *   const components: any = [ResponsiveBaseMapLayer]
 *   components.push(BoundaryLayer);
 *
 * ```
 *
 */
export const BoundaryLayer = ({
  map,
  view,
  isFMTracking = false,
  boundaryLayerProps: {
    boundary
  }
}: Props) => {
  const layerRef = useRef(null);
  
  useEffect(() => {
    setInitialView();
  }, [boundary]);

  const setInitialView = useCallback(async() => {
    const {Graphic, GraphicsLayer} = await getEsriModules(isFMTracking);
  
    if (layerRef.current) {
      map.layers.remove(layerRef.current);
      layerRef.current = null;
    }
    if (boundary?.length) {
      removeLayerIfExist(map, BOUNDARY_MAP_LAYER_ID);
      // Create a new GraphicsLayer and add it to the map
      layerRef.current = new GraphicsLayer({
        id    : BOUNDARY_MAP_LAYER_ID,
        title : BOUNDARY_MAP_LAYER_ID
      });
      map.add(layerRef.current);
      map.layers.reorder(layerRef.current, 1);

      const polyline = {
        type             : 'polygon',
        rings            : boundary,
        spatialReference : view.spatialReference
      };
      const graphicLine = new Graphic({
        geometry : polyline,
        symbol   : DEFAULT_SYMBOL()
      });

      layerRef.current.add(graphicLine);
      view.goTo(graphicLine, mapViewGoToOptions).then(function() {
        view.zoom = view.zoom - 1;
      });
    }
  }, [map, view, boundary]);

  return null;
};

export default memo(BoundaryLayer);
